/**
 * useRunMoonPhase — TanStack Query mutation for the Moon Phase Soulmate rite.
 *
 * Calls POST /api/v1/sku/moon-phase-soulmate/run with an empty body.
 * Returns:
 *   201 (first run): { run: { id, output, createdAt }, cached: false }
 *   200 (cached <24h): { run: <full row>, cached: true }
 *
 * Output shape:
 *   { phase, phaseName, lunarAge, illumination, archetypeName, archetypeBlurb }
 *
 * On 400 birth_data_required: surfaces a BirthDataRequiredError so callers
 * can redirect the user to /onboarding/birth.
 */

import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface MoonPhaseOutput {
  phase: string;
  phaseName: string;
  lunarAge: number;
  illumination: number;
  archetypeName: string;
  archetypeBlurb: string;
}

export interface MoonPhaseRunResult {
  run: {
    id: string;
    output: MoonPhaseOutput;
    createdAt: string;
  };
  cached: boolean;
}

export class BirthDataRequiredError extends Error {
  readonly code = 'birth_data_required' as const;
  constructor() {
    super('Birth data is required to run this flow');
    this.name = 'BirthDataRequiredError';
  }
}

export class MoonPhaseRunError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'MoonPhaseRunError';
  }
}

// --------------------------------------------------------------------------
// Hook
// --------------------------------------------------------------------------

export function useRunMoonPhase() {
  const jwt = useAuth((s) => s.jwt);

  const mutation = useMutation<MoonPhaseRunResult, Error>({
    mutationFn: async () => {
      if (!jwt) {
        throw new MoonPhaseRunError(401, 'missing_authorization');
      }

      const res = await apiFetch('/api/v1/sku/moon-phase-soulmate/run', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as { code?: string };
        if (body.code === 'birth_data_required') {
          throw new BirthDataRequiredError();
        }
        throw new MoonPhaseRunError(400, `run_failed_400: ${JSON.stringify(body)}`);
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new MoonPhaseRunError(res.status, `run_failed_${res.status}: ${text}`);
      }

      return (await res.json()) as MoonPhaseRunResult;
    },
  });

  return {
    mutate: mutation.mutate,
    data: mutation.data,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
